/**
 * Mining — history Jita-valuated.
 *
 * Para cada ore que minaste en los últimos 30 días:
 *   - "Jita ese día" = average de sell orders en Jita (ESI history)
 *   - "Jita hoy"     = average actual
 *   - Δ%             = tendencia del precio desde el día que minaste
 *
 * El trend es la pregunta real: "¿los precios de mis ores subieron o
 * bajaron desde que los miné?" — decisión automática sin input.
 *
 * Caveat: ESI history usa sell-order avg (no buy orders), así que el ISK
 * exacto sobreestima ~3-10% vs lo que cobrarías como seller.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { LastUpdated } from '../components/LastUpdated'
import { RefreshButton } from '../components/RefreshButton'
import { LoadingSkeleton } from '../components/LoadingSkeleton'
import { MiningHistory } from '../components/MiningHistory'
import { useForceRefetch } from '../hooks/useForceRefetch'

type Entry = {
  date: string
  type_id: number
  name: string
  quantity: number
  unit_price_at_day: number | null
  unit_price_today: number | null
  value_at_day: number | null
  value_today: number | null
  delta_pct: number | null
}

export function Mining() {
  const { key: forceRefetchKey, force, bump } = useForceRefetch()
  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['mining-valued', forceRefetchKey],
    queryFn: () => api.miningValued(force),
  })

  // Agrupa entries por día (ESI puede emitir varias filas/día por el mismo
  // type si hay varias sesiones de mining).
  const aggregated = useMemo(() => {
    if (!data?.entries) return []
    const byDay = new Map<
      string,
      {
        totalQty: number
        totalAtDay: number
        totalToday: number
        types: Entry[]
      }
    >()
    for (const e of data.entries) {
      const day = e.date.slice(0, 10)
      if (!byDay.has(day)) {
        byDay.set(day, { totalQty: 0, totalAtDay: 0, totalToday: 0, types: [] })
      }
      const bucket = byDay.get(day)!
      // Sumar si ya existe el mismo type_id este día
      const existing = bucket.types.find((x) => x.type_id === e.type_id)
      if (existing) {
        existing.quantity += e.quantity
        // Al re-calcular value, usamos el precio avg de los dos entries
        // (mismo día, asumimos mismo price_at_day y today).
        existing.value_at_day = sumWithNull(
          existing.value_at_day,
          e.unit_price_at_day === null ? null : e.unit_price_at_day * (e.quantity),
        )
        existing.value_today = sumWithNull(
          existing.value_today,
          e.unit_price_today === null ? null : e.unit_price_today * (e.quantity),
        )
        // Para el precio unitario, dejamos el del primer entry (asumimos
        // mismo día = mismo ESI snapshot promedio).
      } else {
        bucket.types.push({ ...e })
      }
    }

    for (const bucket of byDay.values()) {
      bucket.totalQty = bucket.types.reduce((a, t) => a + t.quantity, 0)
      bucket.totalAtDay = bucket.types.reduce(
        (a, t) => a + (t.value_at_day ?? 0),
        0,
      )
      bucket.totalToday = bucket.types.reduce(
        (a, t) => a + (t.value_today ?? 0),
        0,
      )
    }

    return Array.from(byDay.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([day, b]) => ({
        day,
        ...b,
        types: b.types.sort((a, b) => b.quantity - a.quantity),
      }))
  }, [data])

  const s = data?.summary
  const deltaPct = s?.delta_pct ?? null
  const deltaPositive = deltaPct !== null && deltaPct > 0

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Mining</h1>
          <p className="text-eve-muted text-sm">
            Últimos 30 días de mining, valuado a Jita al día que minaste vs
            hoy.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      {/* Summary tiles — comparativa "entonces vs ahora" */}
      {s && s.total_quantity > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <SummaryTile
            label="Minado (30d)"
            value={s.total_quantity.toLocaleString('en-US')}
            suffix=" unidades"
          />
          <SummaryTile
            label="Valor al día"
            value={formatISKShort(s.total_value_at_day)}
            sub="lo que hubiera rendido al toque"
          />
          <SummaryTile
            label="Valor hoy"
            value={formatISKShort(s.total_value_today)}
            sub={`${
              s.skipped_types > 0
                ? `${s.skipped_types} tipos omitidos (cap)`
                : 'todo el ore valuado'
            }`}
            accent
          />
          <SummaryTile
            label="Δ (hoy − al día)"
            value={
              s.delta_pct !== null
                ? `${s.delta_pct >= 0 ? '+' : ''}${s.delta_pct.toFixed(1)}%`
                : '—'
            }
            sub={
              deltaPct === null
                ? '—'
                : deltaPositive
                  ? 'precios subieron'
                  : deltaPct < 0
                    ? 'precios bajaron'
                    : 'sin cambio'
            }
            positive={deltaPositive}
            negative={deltaPct !== null && deltaPct < 0}
          />
        </div>
      )}

      {/* History agregada — totals por día + por ore */}
      <MiningHistory />

      <div className="card">
        {isLoading ? (
          <LoadingSkeleton variant="rows" rows={6} />
        ) : !aggregated.length ? (
          <div className="text-eve-muted py-8 text-center">
            Sin actividad de mining en los últimos 30 días.
          </div>
        ) : (
          <div className="space-y-4">
            {aggregated.map((bucket) => (
              <div
                key={bucket.day}
                className="border-b border-eve-border/50 pb-3 last:border-b-0"
              >
                <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                  <div className="font-mono text-sm">{bucket.day}</div>
                  <div className="flex items-baseline gap-3 text-xs text-eve-muted">
                    <span>
                      al día:{' '}
                      <span className="text-eve-text font-mono">
                        {formatISK(bucket.totalAtDay, 0)}
                      </span>
                    </span>
                    <span>·</span>
                    <span>
                      hoy:{' '}
                      <span className="text-eve-accent font-mono">
                        {formatISK(bucket.totalToday, 0)}
                      </span>
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {bucket.types.map((t) => (
                    <TypePill key={t.type_id} t={t} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-eve-muted">
        ESI market history usa <strong>avg de sell orders</strong> en Jita (no
        buy orders reales) — el ISK exacto sobreestima ~3-10% vs lo que
        cobrarías como seller. El trend (Δ%) sí es directionalmente correcto.
      </p>
    </div>
  )
}

function TypePill({ t }: { t: Entry }) {
  const hasComp = t.value_at_day !== null && t.value_today !== null
  const delta = t.delta_pct
  const positive = delta !== null && delta > 0
  return (
    <span
      className="text-xs bg-eve-bg border border-eve-border px-2 py-0.5 rounded font-mono"
      title={
        hasComp
          ? `${t.quantity.toLocaleString('en-US')} u × ${formatISK(t.unit_price_at_day ?? 0, 2)} ese día (${formatISK(t.value_at_day ?? 0, 0)}), hoy ${formatISK(t.unit_price_today ?? 0, 2)} (${formatISK(t.value_today ?? 0, 0)})`
          : `${t.quantity.toLocaleString('en-US')} u (sin histórico)`
      }
    >
      <span className="text-eve-text">{t.name}</span>:{' '}
      {t.quantity.toLocaleString('en-US')}
      {hasComp && (
        <>
          {' '}
          <span
            className={
              positive
                ? 'text-eve-success'
                : delta !== null && delta < 0
                  ? 'text-eve-danger'
                  : 'text-eve-muted'
            }
          >
            ({delta! >= 0 ? '+' : ''}
            {delta!.toFixed(1)}%)
          </span>
        </>
      )}
    </span>
  )
}

function SummaryTile({
  label,
  value,
  sub,
  suffix,
  accent,
  positive,
  negative,
}: {
  label: string
  value: string
  sub?: string
  suffix?: string
  accent?: boolean
  positive?: boolean
  negative?: boolean
}) {
  const valueClass = positive
    ? 'text-eve-success'
    : negative
      ? 'text-eve-danger'
      : accent
        ? 'text-eve-accent'
        : 'text-eve-text'
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className={`stat mt-1 ${valueClass}`}>
        {value}
        {suffix && (
          <span className="text-sm text-eve-muted ml-1 font-sans">{suffix}</span>
        )}
      </div>
      {sub && <div className="text-xs text-eve-muted mt-1 italic">{sub}</div>}
    </div>
  )
}

// Helper para sumar dos ISKs opcionales (None-wins-over-None conservador)
function sumWithNull(a: number | null, b: number | null): number | null {
  if (a === null && b === null) return null
  return (a ?? 0) + (b ?? 0)
}
